import { useEffect, useState, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/shared/stores/auth-store';
import { setSessionToken } from '@/shared/services/session-token';
import { fetchSession, fetchOrgSyncStatus } from '../../api/auth-api';
import type { SessionResponse } from '../../api/auth-api';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import styles from './SSOCallbackPage.module.scss';

const ORG_SYNC_POLL_INTERVAL = 2000;
const ORG_SYNC_MAX_ATTEMPTS = 60;

function navigateWithSession(
  session: SessionResponse,
  setSession: (params: {
    user: SessionResponse['user'];
    permissions: SessionResponse['permissions'];
    orgId: SessionResponse['selectedOrganizationId'];
    organizations: SessionResponse['organizations'];
  }) => void,
  navigate: ReturnType<typeof useNavigate>,
) {
  setSessionToken(session.token);
  setSession({
    user: session.user,
    permissions: session.permissions,
    orgId: session.selectedOrganizationId,
    organizations: session.organizations,
  });

  if (session.selectedOrganizationId) {
    void navigate({ to: '/promote', replace: true });
  } else {
    void navigate({ to: '/select-organization', replace: true });
  }
}

export function SSOCallbackPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'authenticating' | 'syncing-orgs'>('authenticating');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedRef = useRef(false);

  useEffect(() => {
    async function processCallback() {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        setError('No authentication token received.');
        setTimeout(() => navigate({ to: '/login', replace: true }), 2000);
        return;
      }

      try {
        setSessionToken(token);
        const session = await fetchSession();

        // If orgs already available (e.g. returning user with cached orgs), navigate immediately
        if (session.organizations.length > 0) {
          navigateWithSession(session, setSession, navigate);
          return;
        }

        // Orgs not ready yet — poll for sync completion
        setPhase('syncing-orgs');
        let attempts = 0;

        pollRef.current = setInterval(async () => {
          if (completedRef.current) return;
          attempts++;
          try {
            const syncResult = await fetchOrgSyncStatus();
            if (completedRef.current) return;

            if (syncResult.status === 'complete') {
              completedRef.current = true;
              if (pollRef.current) clearInterval(pollRef.current);
              navigateWithSession(syncResult.session, setSession, navigate);
              return;
            }
          } catch {
            // Retry on the next interval.
          } finally {
            // Enforce the cap here so it still runs when the fetch throws.
            if (!completedRef.current && attempts >= ORG_SYNC_MAX_ATTEMPTS) {
              completedRef.current = true;
              if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
              setError('Organization sync timed out. Please try logging in again.');
              setTimeout(() => navigate({ to: '/login', replace: true }), 3000);
            }
          }
        }, ORG_SYNC_POLL_INTERVAL);
      } catch {
        setError('Authentication failed. Redirecting to login...');
        setTimeout(() => navigate({ to: '/login', replace: true }), 2000);
      }
    }

    void processCallback();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [navigate, setSession]);

  if (error) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>{error}</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <LoadingSpinner size="lg" />
      <p className={styles.message}>
        {phase === 'authenticating' ? 'Completing sign in...' : 'Loading your organizations...'}
      </p>
    </div>
  );
}
